// Dates are stored as instants and shown in the HOTEL's time zone (DR-30): a Chilean group in Bariloche sees Argentine
// local time for its stay. Formatting is the browser's Intl (no date library needed).

export function formatInZone(iso: string, timeZone: string, locale = 'es'): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatDateRangeInZone(fromIso: string, toIso: string, timeZone: string, locale = 'es'): string {
  const format = new Intl.DateTimeFormat(locale, { timeZone, day: 'numeric', month: 'short' });
  return `${format.format(new Date(fromIso))} – ${format.format(new Date(toIso))}`;
}

/** Offset (minutes east of UTC) that `timeZone` has at the instant `at`. */
function offsetMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const value = (type: string): number => Number(parts.find((part) => part.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  );
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/**
 * A wall-clock time AT THE HOTEL ("2031-12-01T15:00", as typed in a form) as an ISO 8601 instant with that zone's
 * offset ("2031-12-01T15:00:00-03:00"). The operator thinks in hotel time, never in their own browser's zone (DR-30).
 * Returns undefined for a malformed input. A time skipped by a DST jump resolves to the instant after the jump.
 */
export function zonedDateTimeToIso(local: string, timeZone: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (match === null) return undefined;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [number, number, number, number, number];
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  // Two passes: the offset at the first guess can differ from the offset at the real instant around a DST change.
  let offset = offsetMinutes(new Date(wall), timeZone);
  offset = offsetMinutes(new Date(wall - offset * 60_000), timeZone);
  const sign = offset < 0 ? '-' : '+';
  const abs = Math.abs(offset);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${local}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
